/**
 * Pure helpers for TO/CC recipient pickers in the Email Flow configuration dialog.
 * @module emailFlowRecipient.helper
 * @author Sejal
 */
import { RECIPIENT_TYPES } from "./emailFlowConfig.constants";
import { isValidEmail } from "./emailFlowConfig.utils";

/**
 * Returns whether `email` is already present on a recipient list (case-insensitive on `Email`).
 * Rows with no `email` (e.g. some system entries) are not matched.
 *
 * @param {Array<{ Email?: string | null }>} recipients - Current chip list
 * @param {string} [email] - Email to check
 * @returns {boolean}
 * @author Sejal
 */
export function isRecipientEmailSelected(recipients, email) {
  if (!email) return false;
  return recipients.some((r) => r.Email?.toLowerCase() === email.toLowerCase());
}

/**
 * Excludes predefined system options that are already selected, then optionally filters by input text.
 *
 * @param {Array<{ id: string, label: string }>} systemOptions - e.g. `TO_SYSTEM_OPTIONS` from `emailFlowConfig.constants`
 * @param {Array<{ Type?: string, Name?: string }>} recipients - Current chip list
 * @param {string} inputValue - Current text in the field
 * @returns {Array<{ id: string, label: string }>}
 * @author Sejal
 */
export function filterSystemRecipientOptions(systemOptions, recipients, inputValue) {
  let filtered = systemOptions.filter((opt) => {
    const isSelectedByName = recipients.some((r) => r.Type === RECIPIENT_TYPES.SYSTEM && r.Name === `@${opt.label}`);
    return !isSelectedByName;
  });

  if (inputValue.trim()) {
    const q = inputValue.toLowerCase();
    filtered = filtered.filter((opt) => opt.label.toLowerCase().includes(q));
  }
  return filtered;
}

/**
 * Drops API contact rows whose email is already in `recipients`.
 *
 * @param {Array<{ Email?: string }>} apiSuggestions - Contact search results
 * @param {Array<{ Email?: string | null }>} recipients - Current chip list
 * @returns {Array}
 * @author Sejal
 */
export function filterRecipientApiSuggestions(apiSuggestions, recipients) {
  return apiSuggestions.filter((contact) => !isRecipientEmailSelected(recipients, contact.Email));
}

/**
 * @param {{ id: string, label: string }} option - Predefined system row from TO_SYSTEM_OPTIONS
 * @returns {Object} Recipient object for React state (TO or CC)
 * @author Sejal
 */
export function buildPredefinedSystemRecipient(option) {
  return {
    Name: `@${option.label}`,
    Email: null,
    TemporaryContact: null,
    PhotoLink: null,
    PhotoLinks: null,
    Id: option.id,
    Type: RECIPIENT_TYPES.SYSTEM,
  };
}

/**
 * @param {Object} contact - Contact from `searchEmailContacts`
 * @returns {Object} Recipient object for React state (TO or CC)
 * @author Sejal
 */
export function buildContactRecipientPayload(contact) {
  return {
    Name: contact.Name,
    Email: contact.Email,
    TemporaryContact: contact.TemporaryContact,
    PhotoLink: contact.PhotoLink,
    PhotoLinks: contact.PhotoLinks,
    Id: contact.Id,
    Type: contact.Type,
  };
}

/**
 * @param {string} lowerCaseEmail - Normalized lower-case email
 * @param {string} id - Stable unique id for the chip (e.g. `manual-${Date.now()}`)
 * @returns {Object} Manual recipient object for React state (TO or CC)
 * @author Sejal
 */
export function buildManualEmailRecipient(lowerCaseEmail, id) {
  return {
    Name: lowerCaseEmail,
    Email: lowerCaseEmail,
    TemporaryContact: null,
    PhotoLink: null,
    PhotoLinks: null,
    Id: id,
    Type: RECIPIENT_TYPES.MANUAL,
  };
}

/**
 * True if a system predefined row with the same display label is already in the list.
 *
 * @param {Array<{ Name?: string }>} recipients
 * @param {{ label: string }} option
 * @returns {boolean}
 * @author Sejal
 */
export function hasPredefinedRecipientWithLabel(recipients, option) {
  return recipients.some((r) => r.Name === `@${option.label}`);
}

/**
 * Validates a manually entered email for add-to-list: non-empty, format, and duplicate vs `recipients`.
 *
 * @param {string} trimmedEmail - Trimmed input
 * @param {Array} recipients - Current chip list
 * @returns {{ ok: true, lowerCaseEmail: string } | { ok: false, error?: string }}
 *   When `ok` is false and `error` is omitted, the caller should no-op silently (e.g. empty string).
 * @author Sejal
 */
export function validateManualRecipientEntry(trimmedEmail, recipients) {
  if (!trimmedEmail) {
    return { ok: false };
  }
  if (!isValidEmail(trimmedEmail)) {
    return { ok: false, error: `"${trimmedEmail}" is not a valid email address` };
  }
  if (isRecipientEmailSelected(recipients, trimmedEmail)) {
    return { ok: false, error: `"${trimmedEmail}" is already added` };
  }
  return { ok: true, lowerCaseEmail: trimmedEmail.toLowerCase() };
}

/**
 * Returns a new array without the recipient whose `Id` matches `recipientId`.
 *
 * @param {Array<{ Id: string }>} recipients
 * @param {string} recipientId
 * @returns {Array}
 * @author Sejal
 */
export function removeRecipientById(recipients, recipientId) {
  return recipients.filter((r) => r.Id !== recipientId);
}

/**
 * @param {KeyboardEvent | { key: string, preventDefault: function(): void }} e - Keyboard event from the input
 * @param {RecipientFieldKeyDownContext} ctx
 * @author Sejal
 */
export function handleRecipientFieldKeyDown(e, ctx) {
  const {
    filteredPredefined,
    filteredApi,
    inputValue,
    recipients,
    selectedIndex,
    setSelectedIndex,
    onSelectPredefined,
    onSelectApi,
    onAddManual,
    onRemoveLastRecipient,
    onEscape,
  } = ctx;

  const totalOptions = filteredPredefined.length + filteredApi.length;

  if (e.key === "Enter") {
    e.preventDefault();
    if (selectedIndex >= 0 && selectedIndex < totalOptions) {
      if (selectedIndex < filteredPredefined.length) {
        onSelectPredefined(filteredPredefined[selectedIndex]);
      } else {
        onSelectApi(filteredApi[selectedIndex - filteredPredefined.length]);
      }
    } else if (inputValue.trim()) {
      onAddManual(inputValue);
    }
  } else if (e.key === "Backspace" && !inputValue && recipients.length > 0) {
    onRemoveLastRecipient();
  } else if (e.key === "ArrowDown") {
    e.preventDefault();
    setSelectedIndex((prev) => (prev < totalOptions - 1 ? prev + 1 : prev));
  } else if (e.key === "ArrowUp") {
    e.preventDefault();
    setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
  } else if (e.key === "Escape") {
    onEscape();
  } else if (e.key === "," || e.key === ";") {
    e.preventDefault();
    if (inputValue.trim()) {
      onAddManual(inputValue);
    }
  }
}
